import { useCallback, useMemo } from 'react';

import { getOwnershipDisplayName } from '../components/ownership/utils';
import { usePropertyLists } from '../contexts/PropertyListsContext';
import type { Read } from '../lib/readOf';

/**
 * Which side of the ownership split a flow serves. The two are disjoint and
 * between them cover every ownership: an association (HOA/COA) carries
 * assessments, a group (individual/llc/trust) carries rent rolls.
 */
export type OwnershipKind = 'group' | 'association';

/**
 * Which properties a flow may offer, said in whichever terms the caller has.
 * A flow opened from a portfolio card knows its portfolio; one opened for an
 * entity knows its ownership; the onboarding flows know only which side of the
 * split they serve. All three reduce to a set of portfolios and their properties.
 *
 * `rentalOwnership` is for a flow that the server refuses on an association, such as Rent Radar.
 * It offers a property only when its ownership is in the user's ownerships and is not an
 * association. The `group` kind cannot say this: a portfolio whose ownership the user cannot read
 * has no entry there to show it is an association, so `group` takes it as a group.
 */
export type PropertyScope =
  | { kind: 'ownershipKind'; ownershipKind: OwnershipKind }
  | { kind: 'ownership'; ownershipId: string }
  | { kind: 'portfolio'; portfolioId: string }
  | { kind: 'rentalOwnership' };

/** One entry of a Select. */
export interface SelectOption {
  value: string;
  label: string;
}

export interface PropertyOptions {
  /**
   * How far the three reads behind the options have got, with the properties in scope as its
   * data. An empty `propertyOptions` means "none" only when this is `ready`. Before that it means
   * "not loaded", or "could not load". A failed refresh over loaded options keeps them `ready`.
   */
  read: Read<SelectOption[]>;
  /** Properties in scope, as Select options, labelled "Ownership: Portfolio: Address". */
  propertyOptions: SelectOption[];
  /** Portfolios in scope — what a new property may be added to. */
  portfolioOptions: SelectOption[];
  /** Property display id → its owning ownership's display id. */
  ownershipIdByPropertyId: Map<string, string | null>;
  /**
   * Portfolio a new property should default to: the given property's own
   * portfolio, else the first in scope, else '' when there is nowhere to add
   * one yet.
   */
  defaultPortfolioIdFor: (propertyId: string | null) => string;
}

/**
 * The properties and portfolios one flow can see, given how it is scoped.
 *
 * Sole owner of the scoping rule, so the picker and the add-property form
 * beneath it cannot disagree about what is in scope — offering a property the
 * form could not have created, or a portfolio the picker would never list.
 */
export function usePropertyOptions(scope: PropertyScope): PropertyOptions {
  // Narrowed to primitives up front: callers build the scope inline, so it is a
  // fresh object every render and would defeat every memo keyed on it.
  const scopeKind = scope.kind;
  const scopeOwnershipKind = scope.kind === 'ownershipKind' ? scope.ownershipKind : null;
  const scopeOwnershipId = scope.kind === 'ownership' ? scope.ownershipId : null;
  const scopePortfolioId = scope.kind === 'portfolio' ? scope.portfolioId : null;

  // The user's own lists, or one organization's lists in the admin onboarding flow.
  const { properties: userProperties, portfolios, ownerships, read: loaded } = usePropertyLists();

  const ownershipNameById = useMemo(
    () => new Map((ownerships ?? []).map((o) => [o.id, getOwnershipDisplayName(o)])),
    [ownerships],
  );
  const ownershipIdByPortfolioId = useMemo(
    () => new Map((portfolios ?? []).map((p) => [p.id, p.ownershipId])),
    [portfolios],
  );
  const associationOwnershipIds = useMemo(
    () =>
      new Set(
        (ownerships ?? [])
          .filter((o) => o.profile.ownershipProfileType === 'association')
          .map((o) => o.id),
      ),
    [ownerships],
  );

  const scopedPortfolios = useMemo(
    () =>
      (portfolios ?? []).filter((p) => {
        if (scopeKind === 'portfolio') return p.id === scopePortfolioId;
        // Every other scope is expressed in ownership terms, so a portfolio with
        // no ownership can never be in one.
        if (!p.ownershipId) return false;
        if (scopeKind === 'ownership') return p.ownershipId === scopeOwnershipId;
        if (scopeKind === 'rentalOwnership') {
          return (
            ownershipNameById.has(p.ownershipId) && !associationOwnershipIds.has(p.ownershipId)
          );
        }
        return (
          associationOwnershipIds.has(p.ownershipId) === (scopeOwnershipKind === 'association')
        );
      }),
    [
      portfolios,
      ownershipNameById,
      associationOwnershipIds,
      scopeKind,
      scopePortfolioId,
      scopeOwnershipId,
      scopeOwnershipKind,
    ],
  );
  const scopedPortfolioIds = useMemo(
    () => new Set(scopedPortfolios.map((p) => p.id)),
    [scopedPortfolios],
  );

  const ownershipIdByPropertyId = useMemo(
    () =>
      new Map(
        (userProperties ?? []).map((p) => [
          p.property.id,
          ownershipIdByPortfolioId.get(p.portfolio.id) ?? null,
        ]),
      ),
    [userProperties, ownershipIdByPortfolioId],
  );

  const propertyOptions = useMemo(
    () =>
      (userProperties ?? [])
        .filter((p) => scopedPortfolioIds.has(p.portfolio.id))
        .map((p) => {
          const { street, city, state, zipCode } = p.property.address;
          const ownershipName = ownershipNameById.get(
            ownershipIdByPortfolioId.get(p.portfolio.id) ?? '',
          );
          const prefix = ownershipName ? `${ownershipName}: ` : '';
          return {
            value: p.property.id,
            label: `${prefix}${p.portfolio.name}: ${street}, ${city}, ${state} ${zipCode}`,
          };
        }),
    [userProperties, scopedPortfolioIds, ownershipIdByPortfolioId, ownershipNameById],
  );

  const portfolioOptions = useMemo(
    () => scopedPortfolios.map((p) => ({ value: p.id, label: p.name })),
    [scopedPortfolios],
  );

  const defaultPortfolioIdFor = useCallback(
    (propertyId: string | null) =>
      userProperties?.find((p) => p.property.id === propertyId)?.portfolio.id ??
      portfolioOptions[0]?.value ??
      '',
    [userProperties, portfolioOptions],
  );

  const read: Read<SelectOption[]> =
    loaded.kind === 'ready'
      ? { kind: 'ready', data: propertyOptions, refreshFailed: loaded.refreshFailed }
      : loaded;

  return {
    read,
    propertyOptions,
    portfolioOptions,
    ownershipIdByPropertyId,
    defaultPortfolioIdFor,
  };
}
